import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { PHONE_TEL, IMAGES } from "../content/siteContent";
import { IconCheck, IconPhone, IconWhatsApp } from "./Icons";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => {
    const reduce = useReducedMotion();
    if (reduce) return <span className="block">{children}</span>;
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
};

const Fade = ({ children, delay, className = "" }) => {
    const reduce = useReducedMotion();
    if (reduce) return <div className={className}>{children}</div>;
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay, ease: EASE }}
        >
            {children}
        </motion.div>
    );
};

const SLOGANS = [
    { text: "MOVEMENT IS MEDICINE", lang: "en" },
    { text: "الحركة هي الدواء", lang: "ar" },
];

const TYPE_MS = 130;
const ERASE_MS = 55;
const HOLD_MS = 3500;
const PAUSE_MS = 1200;

const TypeLoop = () => {
    const reduce = useReducedMotion();
    const { isRtl } = useLanguage();
    const slogan = isRtl ? SLOGANS[1] : SLOGANS[0];
    const [len, setLen] = useState(0);
    const [phase, setPhase] = useState("typing");

    useEffect(() => {
        setLen(0);
        setPhase("typing");
    }, [isRtl]);

    useEffect(() => {
        if (reduce) return undefined;
        const { text } = slogan;
        if (phase === "typing" && len >= text.length) {
            setPhase("holding");
            return undefined;
        }
        if (phase === "erasing" && len === 0) {
            setPhase("pausing");
            return undefined;
        }
        let timer;
        if (phase === "typing") {
            timer = setTimeout(() => setLen((v) => v + 1), TYPE_MS);
        } else if (phase === "holding") {
            timer = setTimeout(() => setPhase("erasing"), HOLD_MS);
        } else if (phase === "erasing") {
            timer = setTimeout(() => setLen((v) => v - 1), ERASE_MS);
        } else {
            timer = setTimeout(() => setPhase("typing"), PAUSE_MS);
        }
        return () => clearTimeout(timer);
    }, [phase, len, slogan, reduce]);

    const { text, lang } = slogan;
    const dir = lang === "ar" ? "rtl" : "ltr";
    const langClass = lang === "ar" ? " tp-typeloop-ar" : "";

    if (reduce) {
        return (
            <span dir={dir} className={`tp-typeloop-static${langClass}`}>
                {text}
            </span>
        );
    }

    return (
        <span dir={dir} className={`tp-typeloop${langClass}`} data-testid="hero-typeloop">
            {text.slice(0, len) || "\u00A0"}
        </span>
    );
};

const Hero = () => {
    const { t, isRtl } = useLanguage();
    const reduce = useReducedMotion();

    return (
        <section className="relative overflow-hidden pt-24 md:pt-32" aria-labelledby="hero-title" data-testid="hero-section">
            <div
                aria-hidden="true"
                className="animate-drift-slow pointer-events-none absolute -top-32 end-[-10%] h-[34rem] w-[34rem] rounded-full opacity-70 blur-3xl"
                style={{ background: "radial-gradient(circle, rgba(178,143,55,0.22) 0%, rgba(0,77,77,0.10) 45%, transparent 70%)" }}
            />
            <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 pb-12 sm:px-6 md:pb-16 lg:grid-cols-12 lg:gap-10 lg:px-8">
                <div className="lg:col-span-7">
                    <Fade delay={0.05}>
                        <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.24em] text-gold-600">
                            <span className="inline-block h-px w-10 bg-gold-500" aria-hidden="true" />
                            {t.hero.overline}
                        </p>
                    </Fade>
                    <h1
                        id="hero-title"
                        className="mt-6 font-display text-4xl font-medium leading-[1.15] tracking-tight text-ink sm:text-5xl lg:text-6xl lg:leading-[1.08]"
                        data-testid="hero-title"
                    >
                        {t.hero.titleLines.map((line, i) => (
                            <MaskedLine key={line} delay={0.15 + i * 0.14}>
                                {i === t.hero.titleLines.length - 1 ? (
                                    <span className="text-pine">{line}</span>
                                ) : (
                                    line
                                )}
                            </MaskedLine>
                        ))}
                    </h1>
                    <Fade delay={0.35}>
                        <p
                            className="mt-5 font-mono text-sm font-bold uppercase tracking-[0.22em] text-gold-600 md:text-base"
                            data-testid="hero-typeline"
                        >
                            <TypeLoop />
                        </p>
                    </Fade>
                    <Fade delay={0.45}>
                        <p className="mt-6 max-w-xl text-base leading-relaxed text-mist md:text-lg">{t.hero.subtitle}</p>
                    </Fade>
                    <Fade delay={0.6} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                        <a
                            href="#appointment"
                            data-testid="hero-book-cta"
                            className="inline-flex min-h-[52px] items-center justify-center gap-2.5 rounded-full bg-gold-500 px-8 text-base font-semibold text-ink shadow-[0_14px_30px_-12px_rgba(178,143,55,0.55)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-gold-600 hover:shadow-[0_18px_36px_-12px_rgba(178,143,55,0.6)] active:scale-95"
                        >
                            <IconWhatsApp className="h-5 w-5" />
                            {t.hero.ctaPrimary}
                        </a>
                        <a
                            href={`tel:${PHONE_TEL}`}
                            data-testid="hero-call-cta"
                            className="inline-flex min-h-[52px] items-center justify-center gap-2.5 rounded-full border border-pine/25 px-8 text-base font-semibold text-pine transition-[border-color,color,transform] duration-300 hover:border-gold-500 hover:text-gold-600 active:scale-95"
                        >
                            <IconPhone className="h-4 w-4" />
                            <span dir="ltr">{t.hero.ctaSecondary}</span>
                        </a>
                    </Fade>
                    <Fade delay={0.75}>
                        <p className="mt-7 flex items-center gap-2 text-sm font-medium text-pine">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold-100 text-gold-600">
                                <IconCheck className="h-3.5 w-3.5" />
                            </span>
                            {t.hero.badge}
                        </p>
                    </Fade>
                </div>

                <div className="relative lg:col-span-5">
                    <motion.figure
                        initial={reduce ? false : { clipPath: isRtl ? "inset(0 0 0 100%)" : "inset(0 100% 0 0)" }}
                        animate={reduce ? {} : { clipPath: "inset(0 0% 0 0%)" }}
                        transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
                        className="relative"
                    >
                        <div
                            aria-hidden="true"
                            className="absolute -inset-3 rounded-[2.2rem] rounded-ee-none border border-gold-500/50 sm:-inset-4"
                        />
                        <div className="relative overflow-hidden rounded-[2rem] rounded-ee-none shadow-[0_35px_70px_-30px_rgba(0,77,77,0.45)]">
                            <img
                                src={IMAGES.hero}
                                alt={t.hero.imageAlt}
                                width="1041"
                                height="1920"
                                fetchpriority="high"
                                className="animate-hero-zoom aspect-[4/5] w-full object-cover"
                                data-testid="hero-image"
                            />
                            <div
                                aria-hidden="true"
                                className="absolute inset-0 bg-gradient-to-t from-pine/35 via-transparent to-transparent"
                            />
                        </div>
                    </motion.figure>
                </div>
            </div>
        </section>
    );
};

export default Hero;
